import type { TesterPresenceItem, TesterMenuGroup } from '@runhuman/shared';
import { MENU_GROUP_LABELS } from '@runhuman/shared';
import { PresenceMemberRow } from './PresenceMemberRow';

interface Props {
  readonly group: TesterMenuGroup;
  readonly testers: readonly TesterPresenceItem[];
  readonly collapsed: boolean;
  readonly onToggle: (group: TesterMenuGroup) => void;
  /**
   * Threaded through to each `<PresenceMemberRow>` to make the row clickable
   * (#3967). When undefined, rows are inert.
   */
  readonly onTesterClick?: (tester: TesterPresenceItem) => void;
}

/**
 * Collapsible group of testers sharing the same presence bucket — mirrors
 * the bucketed sections in the web `<PresenceMenu>`. Empty buckets are
 * elided by the parent so this component always renders at least one row.
 *
 * Header is a `<button>` so screen-readers correctly announce the
 * collapse/expand affordance.
 */
export function PresenceSection({ group, testers, collapsed, onToggle, onTesterClick }: Props) {
  const headerId = `presence-section-${group}-header`;
  const listId = `presence-section-${group}-list`;

  return (
    <section className="presence-section" aria-labelledby={headerId}>
      <button
        id={headerId}
        type="button"
        className="presence-section__header"
        onClick={() => onToggle(group)}
        aria-expanded={!collapsed}
        aria-controls={listId}
      >
        <span className={`presence-section__chevron${collapsed ? ' presence-section__chevron--collapsed' : ''}`} aria-hidden="true">
          ▾
        </span>
        <span className="presence-section__label">{MENU_GROUP_LABELS[group]}</span>
        <span className="presence-section__count">{testers.length}</span>
      </button>
      {!collapsed && (
        <ul id={listId} className="presence-section__list">
          {testers.map((t) => (
            <PresenceMemberRow key={t.id} tester={t} group={group} onClick={onTesterClick} />
          ))}
        </ul>
      )}
    </section>
  );
}
